import React, { useState } from "react";
import { Ban, Trash2, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DURATIONS = [[7, "7 days"], [30, "30 days"], [0, "Permanent"]];

export const isSuspended = (u) => u.suspended_until && (u.suspended_until === "permanent" || u.suspended_until > new Date().toISOString());

export function StatusBadge({ u }) {
  if (!isSuspended(u)) return null;
  const label = u.suspended_until === "permanent" ? "Suspended · permanent" : `Suspended until ${new Date(u.suspended_until).toLocaleDateString()}`;
  return <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-400" data-testid={`user-suspended-${u.user_id}`} title={u.suspend_reason}>{label}</span>;
}

export default function UserModeration({ u, onChanged }) {
  const [mode, setMode] = useState(null);
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (u.is_admin) return <span className="text-xs text-white/30">—</span>;

  const run = async () => {
    setBusy(true);
    try {
      if (mode === "suspend") await api.post(`/admin/users/${u.user_id}/suspend`, { days, reason });
      else await api.delete(`/admin/users/${u.user_id}`, { data: { reason } });
      toast.success(mode === "suspend" ? `${u.name} suspended` : `${u.name} deleted`);
      setMode(null); setReason(""); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); } finally { setBusy(false); }
  };
  const unsuspend = async () => { await api.post(`/admin/users/${u.user_id}/unsuspend`); toast.success("Suspension lifted"); onChanged(); };

  return (
    <div className="flex items-center justify-end gap-1">
      {isSuspended(u)
        ? <button onClick={unsuspend} data-testid={`unsuspend-${u.user_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white/70 hover:bg-white/10"><Undo2 size={13} /> Unsuspend</button>
        : <button onClick={() => setMode("suspend")} data-testid={`suspend-${u.user_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-amber-400 hover:bg-amber-500/10"><Ban size={13} /> Suspend</button>}
      <button onClick={() => setMode("delete")} data-testid={`delete-user-${u.user_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-red-500/10"><Trash2 size={13} /> Delete</button>
      <Dialog open={!!mode} onOpenChange={(v) => !v && setMode(null)}>
        <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-md" data-testid="moderation-dialog">
          <DialogHeader>
            <DialogTitle>{mode === "suspend" ? `Suspend ${u.name}` : `Delete ${u.name}`}</DialogTitle>
            <DialogDescription className="text-white/50">{mode === "suspend" ? "The member is signed out and sees this reason when trying to sign in." : "The account is removed and the email is blocked from registering again. This cannot be undone."}</DialogDescription>
          </DialogHeader>
          {mode === "suspend" && (
            <div className="flex gap-2">{DURATIONS.map(([d, l]) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`suspend-days-${d}`} className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${days === d ? "bg-amber-500/20 border-amber-500/50 text-amber-300" : "border-white/10 text-white/60"}`}>{l}</button>
            ))}</div>
          )}
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3} placeholder="Reason (shown to the member)" data-testid="moderation-reason"
            className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none" />
          <button onClick={run} disabled={busy} data-testid="moderation-confirm" className={`w-full py-2.5 rounded-full font-bold disabled:opacity-50 ${mode === "suspend" ? "bg-amber-500 text-black" : "bg-red-500 text-white"}`}>
            {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : mode === "suspend" ? "Suspend account" : "Delete account"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
